import { createFileRoute } from '@tanstack/react-router';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar';
import { seo } from '@/seo/seo';

export const Route = createFileRoute('/_marketing/terms')({
  head: () => ({
    meta: [
      ...seo({
        title: 'Listening | Terms of Service',
        description:
          'Discover the Terms of Service for Listening to understand the guidelines and obligations when using our platform. Stay informed about your rights and responsibilities.',
      }),
    ],
  }),
  component: () => (
    <>
      <NavBar />

      <section className='mx-auto max-w-[1296px] px-[10px] pt-[40px] text-[14px] sm:px-5 md:px-[140px]'>
        <h1 className='mt-5 mb-[10px] text-[2.375rem] leading-[1.15]'>
          Listening Terms of Service
        </h1>
        <div>
          <p className='mb-3'>Last Updated: August 2024</p>
          <p>
            Welcome, and thank you for your interest in The Listening App Inc.
            (“
            <strong>
              <u>Listening</u>
            </strong>
            ,” “
            <strong>
              <u>we</u>
            </strong>
            ,” or “
            <strong>
              <u>us</u>
            </strong>
            ”) and our website at{' '}
            <a
              className='text-blue-600 underline'
              href='http://www.listening.com'
            >
              www.listening.com
            </a>
            , along with our related websites, hosted applications, mobile or
            other downloadable applications, web browser extension, and other
            services provided by us (collectively, the “
            <strong>
              <u>Service</u>
            </strong>
            ”). These Terms of Service are a legally binding contract between
            you and Listening regarding your use of the Service.
          </p>
          <p className='mb-2'>
            These terms constitute an agreement between you and Listening.
            Listening, not Apple nor Google, is solely responsible for the
            content in this app and related Products.
          </p>
          <p className='mb-2'>
            This is a non-transferable license to use the Products on desktop
            computers and mobile devices that you own. For Apple device users,
            this license is pursuant to Usage Rules set forth in the Apple Media
            Services Terms and Conditions, except that such Licensed Application
            may be accessed and used by other accounts associated with the
            purchaser.
          </p>
          <p className='text-center font-sans font-semibold'>
            PLEASE READ THE FOLLOWING TERMS CAREFULLY:
          </p>
          <p>
            <strong>
              BY CLICKING "I ACCEPT," OR BY DOWNLOADING, INSTALLING, OR
              OTHERWISE ACCESSING OR USING THE SERVICE
            </strong>
            , YOU AGREE THAT YOU HAVE READ AND UNDERSTOOD, AND, AS A CONDITION
            TO YOUR USE OF THE SERVICE, YOU AGREE TO BE BOUND BY, THE FOLLOWING
            TERMS AND CONDITIONS, INCLUDING LISTENING'S{' '}
            <a
              className='text-blue-600 underline'
              href='https://www.listening.com/privacy'
            >
              PRIVACY POLICY
            </a>{' '}
            (TOGETHER, THESE "
            <strong>
              <u>TERMS</u>
            </strong>
            "). IF YOU ARE NOT ELIGIBLE, OR DO NOT AGREE TO THE TERMS, THEN YOU
            DO NOT HAVE OUR PERMISSION TO USE THE SERVICE. YOUR USE OF THE
            SERVICE, AND LISTENING'S PROVISION OF THE SERVICE TO YOU,
            CONSTITUTES AN AGREEMENT BY LISTENING AND BY YOU TO BE BOUND BY
            THESE TERMS.
          </p>
          <br />
          <p>
            <strong>ARBITRATION NOTICE</strong>. Except for certain kinds of
            disputes described in Section 17 (Dispute Resolution and
            Arbitration), you agree that disputes arising under these Terms will
            be resolved by binding, individual arbitration, and BY ACCEPTING
            THESE TERMS, YOU AND LISTENING ARE EACH WAIVING THE RIGHT TO A TRIAL
            BY JURY OR TO PARTICIPATE IN ANY CLASS ACTION OR REPRESENTATIVE
            PROCEEDING.
          </p>

          <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
            1. <strong>Listening Service Overview.</strong>
          </h2>
          <p className='mb-3 text-black'>
            Listening's text-to-audio platform converts written materials (such
            as academic papers, documents, websites, and emails) into audio
            clips that users can listen to on the go, or on desktop software.
          </p>
          <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
            2. <strong>Eligibility.</strong>
          </h2>
          <p className='mb-3 text-black'>
            You must be at least 18 years old to use the Service. By agreeing to
            these Terms, you represent and warrant to us that: (a) you are at
            least 18 years old; (b) you have not previously been suspended or
            removed from the Service; and (c) your registration and your use of
            the Service is in compliance with any and all applicable laws and
            regulations. If you are an entity, organization, or company, the
            individual accepting these Terms on your behalf represents and
            warrants that they have authority to bind you to these Terms and you
            agree to be bound by these Terms.
          </p>
          <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
            3. <strong>Accounts and Registration.</strong>
          </h2>
          <p className='mb-3 text-black'>
            To access most features of the Service, you must register for an
            account. When you register for an account, you may be required to
            provide us with some information about yourself, such as your name,
            email address, demographic information, and other information. You
            agree that the information you provide to us is accurate, complete,
            and not misleading, and that you will keep it accurate and up to
            date at all times. When you register, you will be asked to create a
            password. You are solely responsible for maintaining the
            confidentiality of your account and password, and you accept
            responsibility for all activities that occur under your account. If
            you believe that your account is no longer secure, then you should
            immediately notify us at{' '}
            <a
              className='text-blue-600 underline'
              href='mailto:support@listening.com#1665636666796462567a7f656273787f78713875797b'
            >
              <span>support@listening.com</span>
            </a>
            .
          </p>
          <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
            4. <strong>General Payment Terms.</strong>
          </h2>
          <p className='mb-3 text-black'>
            Certain features of the Service may require you to pay fees. Before
            you pay any fees, you will have an opportunity to review and accept
            the fees that you will be charged. Unless otherwise specifically
            provided for in these Terms, all fees are in U.S. Dollars. All
            payments are refundable for up to 30 days, and are non-refundable
            thereafter, except as required by law.
          </p>
          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            4.1. <strong>Price.</strong>
          </h3>
          <p className='mb-3 text-black'>
            Listening reserves the right to determine pricing for the Service.
            Listening will make reasonable efforts to keep pricing information
            published on the Service up to date. We encourage you to check our
            homepage periodically for current pricing information. Listening may
            change the fees for any feature of the Service, including additional
            fees or charges, if Listening gives you advance notice of changes
            before they apply. Listening, at its sole discretion, may make
            promotional offers with different features and different pricing to
            any of Listening's customers. These promotional offers, unless made
            to you, will not apply to your offer or these Terms.
          </p>
          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            4.2. <strong>Authorization.</strong>
          </h3>
          <p className='mb-3 text-black'>
            You authorize Listening to charge all sums for the orders that you
            make and any level of Service you select as described in these Terms
            or published by Listening, including all applicable taxes, to the
            payment method specified in your account. If you pay any fees with a
            credit card, then Listening may seek pre-authorization of your
            credit card account prior to your purchase to verify that the credit
            card is valid and has the necessary funds or credit available to
            cover your purchase. Payment transactions may be processed by a
            third-party payment processor, such as Stripe, Inc. ("
            <strong>
              <u>Stripe</u>
            </strong>
            "), or by the Google Play or Apple App stores (each an "
            <strong>
              <u>App Store</u>
            </strong>
            "), in which case, by making a payment through the Service, you
            agree to be bound by and subject to such payment processor's terms
            of service and privacy policy (which, in the case of Stripe, are
            available at
            <a
              className='text-blue-600 underline'
              href='https://stripe.com/legal/end-users'
            >
              Stripe End User Terms of Service
            </a>{' '}
            and{' '}
            <a
              className='text-blue-600 underline'
              href='https://stripe.com/privacy'
            >
              Stripe Privacy Policy
            </a>
            ). We reserve the right to refuse to process or complete any
            transaction, and to cancel any transaction, in our sole discretion.
          </p>
          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            4.3. <strong>Subscription Service.</strong>
          </h3>
          <p className='mb-3 text-black'>
            The Service may include certain subscription-based plans with
            automatically recurring payments for periodic charges ("
            <strong>
              <u>Subscription Service</u>
            </strong>
            "). The "
            <strong>
              <u>Subscription Billing Date</u>
            </strong>
            " is the date when you purchase your first subscription to the
            Service. The Subscription Service will begin on the Subscription
            Billing Date and continue for the subscription period that you
            select on your account (such period, the "
            <strong>
              <u>Initial Subscription Period</u>
            </strong>
            "), and will automatically renew for successive periods of the same
            duration as the Initial Subscription Period (the Initial
            Subscription Period and each such renewal period, each a "
            <strong>
              <u>Subscription Period</u>
            </strong>
            ") unless you cancel the Subscription Service or we terminate it. If
            you activate a Subscription Service, then you authorize Listening or
            its third-party payment processors to periodically charge, on a
            going-forward basis and until cancellation of the Subscription
            Service, all accrued sums on or before the payment due date. For
            information on the "
            <strong>
              <u>Subscription Fee</u>
            </strong>
            ", please see our{' '}
            <a
              className='text-blue-600 underline'
              href='https://www.listening.com/app/register'
            >
              signup page
            </a>
            . Your account will be charged automatically on the Subscription
            Billing Date and thereafter on the renewal date of your Subscription
            Service for all applicable fees and taxes for the next Subscription
            Period. You must cancel your Subscription Service before it renews
            in order to avoid billing of the next periodic Subscription Fee to
            your account. Listening or its third-party payment processor will
            bill the periodic Subscription Fee to the payment method associated
            with your account or that you otherwise provide to us. If you signed
            up for the Subscription Service on our website, you may cancel the
            Subscription Service by accessing our web app at app.listening.com
            and clicking "Settings". If you signed up for the Subscription
            Service through an App Store, you may cancel the Subscription
            Service through the subscription management page in the applicable
            App Store. You may also cancel the Subscription Service by
            contacting us at{' '}
            <a
              className='text-blue-600 underline'
              href='mailto:support@listening.com'
            >
              <span>support@listening.com</span>
            </a>
            . YOUR CANCELLATION MUST BE RECEIVED BEFORE THE RENEWAL DATE IN
            ORDER TO AVOID CHARGE FOR THE NEXT SUBSCRIPTION PERIOD.
          </p>
          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            4.4. <strong>Delinquent Accounts.</strong>
          </h3>
          <p className='mb-3 text-black'>
            Listening may suspend or terminate access to the Service, including
            fee-based portions of the Service, for any account for which any
            amount is due but unpaid. In addition to the amount due for the
            Service, a delinquent account will be charged with fees or charges
            that are incidental to any chargeback or collection of any the
            unpaid amount, including collection fees. If your payment method is
            no longer valid at the time a renewal Subscription Fee is due, then
            Listening reserves the right to delete your account and any
            information or User Content (defined below) associated with your
            account without any liability to you.
          </p>
          <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
            5. <strong>Licenses.</strong>
          </h2>
          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            5.1. <strong>Limited License.</strong>
          </h3>
          <p className='mb-3 text-black'>
            Subject to your complete and ongoing compliance with these Terms,
            Listening grants you, solely for your personal, non-commercial use,
            a limited, non-exclusive, non-transferable, non-sublicensable,
            revocable license to: (a) install and use one object code copy of
            any mobile or other downloadable application associated with the
            Service (whether installed by you or pre-installed on your mobile
            device by the device manufacturer or a wireless telephone provider)
            on a mobile device that you own or control; and (b) access and use
            the Service.
          </p>
          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            5.2. <strong>License Restrictions.</strong>
          </h3>
          <p className='mb-3 text-black'>
            Except and solely to the extent such a restriction is impermissible
            under applicable law, you may not: (a) reproduce, distribute,
            publicly display, publicly perform, or create derivative works of
            the Service; (b) make modifications to the Service; or (c) interfere
            with or circumvent any feature of the Service, including any
            security or access control mechanism. If you are prohibited under
            applicable law from using the Service, then you may not use it.
          </p>
          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            5.3. <strong>Feedback.</strong>
          </h3>
          <p className='mb-3 text-black'>
            We respect and appreciate the thoughts and comments from our users
            If you choose to provide input and suggestions, including any
            reviews or other public comments, regarding existing
            functionalities, problems with or proposed modifications or
            improvements to the Service ("
            <strong>
              <u>Feedback</u>
            </strong>
            "), then you hereby grant Listening an unrestricted, perpetual,
            irrevocable, non-exclusive, fully-paid, royalty-free right and
            license to exploit the Feedback in any manner and for any purpose,
            including to improve the Service and create other products and
            services. We will have no obligation to provide you with attribution
            for any Feedback you provide to us.
          </p>
          <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
            6. <strong>Ownership; Proprietary Rights.</strong>
          </h2>
          <p className='mb-3 text-black'>
            The Service is owned and operated by Listening. The visual
            interfaces, graphics, design, compilation, information, data,
            computer code (including source code or object code), products,
            software, services, and all other elements of the Service provided
            by Listening ("
            <strong>
              <u>Materials</u>
            </strong>
            ") are protected by intellectual property and other laws. All
            Materials included in the Service are the property of Listening or
            its third-party licensors. Except as expressly authorized by
            Listening, you may not make use of the Materials. There are no
            implied licenses in these Terms and Listening reserves all rights to
            the Materials not granted expressly in these Terms.
          </p>
          <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
            7. <strong>Third-Party Terms.</strong>
          </h2>
          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            7.1. <strong>Third-Party Services and Linked Websites.</strong>
          </h3>

          <p className='mb-3 text-black'>
            Listening may provide tools through the Service that enable you to
            export information, including User Content or documents to
            third-party services, including through features that allow you to
            share documents through links, which can then be shared on third
            party platforms like Twitter or Facebook. By using one of these
            tools, you hereby authorize that Listening to transfer that
            information to the applicable third-party service. Third-party
            services are not under Listening's control, and, to the fullest
            extent permitted by law, Listening is not responsible for any
            third-party service's use of your exported information. The Service
            may also contain links to third-party websites. Linked websites are
            not under Listening's control, and Listening is not responsible for
            their content. Please be sure to review the terms of use and privacy
            policy of any third-party services before you share any User Content
            or information with such third-party services. Once sharing occurs,
            Listening will have no control over the information that has been
            shared.
          </p>

          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            7.2. <strong>Third-Party Software.</strong>
          </h3>
          <p className='mb-3 text-black'>
            The Service may include or incorporate third-party software
            components that are generally available free of charge under
            licenses granting recipients broad rights to copy, modify, and
            distribute those components ("
            <strong>
              <u>Third-Party Components</u>
            </strong>
            "). Although the Service is provided to you subject to these Terms,
            nothing in these Terms prevents, restricts, or is intended to
            prevent or restrict you from obtaining Third-Party Components under
            the applicable third-party licenses or to limit your use of
            Third-Party Components under those third-party
          </p>

          <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
            8. <strong>User Content.</strong>
          </h2>
          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            8.1. <strong>User Content Generally.</strong>
          </h3>

          <p className='mb-3 text-black'>
            Certain features of the Service may permit users to submit, upload,
            publish, broadcast, or otherwise transmit ("
            <strong>
              <u>Post</u>
            </strong>
            ") content to the Service, including messages, documents, text,
            website links, emails, reviews, photos, video or audio (including
            sound or voice recordings and musical recordings embodied in the
            video or audio), images, folders, data, raw text, and any other
            works of authorship or other works ("
            <strong>
              <u>User Content</u>
            </strong>
            "). You retain any copyright and other proprietary rights that you
            may hold in the User Content that you Post to the Service, subject
            to the licenses granted in these Terms.
          </p>

          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            8.2. <strong>Limited License Grant to Listening.</strong>
          </h3>
          <p className='mb-3 text-black'>
            By Posting User Content to or via the Service, you grant Listening a
            worldwide, non-exclusive, irrevocable, royalty-free, fully paid
            right and license (with the right to sublicense through multiple
            tiers) to host, store, transfer, reproduce, modify for the purpose
            of formatting for display, modify for the purpose of formatting for
            audio, create derivative works as authorized in these Terms, and
            distribute your User Content, in whole or in part, in audio and text
            format. You agree to pay all monies owing to any person or entity
            resulting from Posting your User Content and from Listening's
            exercise of the license set forth in this Section.
          </p>

          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            8.3. <strong>Specific Rules for Photographs and Images.</strong>
          </h3>
          <p className='mb-3 text-black'>
            If you Post a photograph or image to the Service that includes one
            or more persons, you hereby grant such persons and their
            administrators, guardians, heirs, and trustees, if any, an
            irrevocable, perpetual, royalty free, fully paid-up, worldwide
            license to reproduce, distribute, and publicly display that
            photograph for personal use and through any online platform or
            service, including the Service, Facebook, Instagram, and Twitter,
            but not to promote any third-party product, good, or service. The
            license contained in this Section does not permit the subject of any
            photo or their administrators, guardians, heirs, or trustees to sell
            that image or photograph, whether on a standalone basis or as
            embodied in any product.
          </p>

          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            8.4.{' '}
            <strong>
              You Must Have Rights to the Content You Post; User Content
              Representations and Warranties.
            </strong>
          </h3>
          <p className='mb-3 text-black'>
            You must not Post User Content if you are not the owner of or are
            not fully authorized to grant rights in all of the elements of that
            User Content. Listening disclaims any and all liability in
            connection with User Content. You are solely responsible for your
            User Content and the consequences of providing User Content via the
            Service. By providing User Content via the Service, you affirm,
            represent, and warrant to us that:
          </p>
          <p className='mb-3 ml-8 text-black'>
            (a) you are the creator and owner of the User Content, or have the
            necessary licenses, rights, consents, and permissions to authorize
            Listening and users of the Service to use and distribute your User
            Content as necessary to exercise the licenses granted by you in this
            Section, in the manner contemplated by Listening, the Service, and
            these Terms;
          </p>
          <p className='mb-3 ml-8 text-black'>
            (b) your User Content, and the Posting or other use of your User
            Content as contemplated by these Terms, does not and will not: (i)
            infringe, violate, misappropriate, or otherwise breach any
            third-party right, including any copyright, trademark, patent, trade
            secret, moral right, privacy right, right of publicity, or any other
            intellectual property, contract, or proprietary right; (ii) slander,
            defame, libel, or invade the right of privacy, publicity or other
            property rights of any other person; or (iii) cause Listening to
            violate any law or regulation or require us to obtain any further
            licenses from or pay any royalties, fees, compensation or other
            amounts or provide any attribution to any third parties;
          </p>
          <p className='mb-3 ml-8 text-black'>
            (c) your User Content could not be deemed by a reasonable person to
            be objectionable, profane, indecent, pornographic, harassing,
            threatening, embarrassing, hateful, or otherwise inappropriate; and
          </p>
          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            8.5. <strong>User Content Disclaimer.</strong>
          </h3>
          <p className='mb-3 text-black'>
            We are under no obligation to edit or control User Content that you
            or other users Post and will not be in any way responsible or liable
            for User Content. Listening may, however, at any time and without
            prior notice, screen, remove, edit, or block any User Content that
            in our sole judgment violates these Terms, is alleged to violate the
            rights of third parties, or is otherwise objectionable. You
            understand that, when using the Service, you will be exposed to User
            Content from a variety of sources and acknowledge that User Content
            may be inaccurate, offensive, indecent, or objectionable. You agree
            to waive, and do waive, any legal or equitable right or remedy you
            have or may have against Listening with respect to User Content. If
            notified by a user or content owner that User Content allegedly does
            not conform to these Terms, we may investigate the allegation and
            determine in our sole discretion whether to remove the User Content,
            which we reserve the right to do at any time and without notice. For
            clarity, Listening does not permit infringing activities on the
            Service.
          </p>

          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            8.6. <strong>Monitoring Content.</strong>
          </h3>
          <p className='mb-3 text-black'>
            Listening does not control and does not have any obligation to
            monitor: (a) User Content; (b) any content made available by third
            parties; or (c) the use of the Service by its users. You acknowledge
            and agree that Listening reserves the right to, and may from time to
            time, monitor any and all information transmitted or received
            through the Service for operational and other purposes. If at any
            time Listening chooses to monitor the content, then Listening still
            assumes no responsibility or liability for content or any loss or
            damage incurred as a result of the use of content. During
            monitoring, information may be examined, recorded, copied, and used
            in accordance with our Privacy Policy (defined below). Listening may
            block, filter, mute, remove or disable access to any User Content
            uploaded to or transmitted through the Service without any liability
            to the user who Posted such User Content to the Service or to any
            other users of the Service.
          </p>

          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            8.7. <strong>Content Sharing and Public Availability.</strong>
          </h3>
          <p className='mb-3 text-black'>
            Uploads to the Service are private by default. However, the Service
            may provide features that enable you to share User Content with
            specific individuals or to make such User Content publicly
            accessible. If you elect to share User Content with a specific
            person or group, you acknowledge and agree that such User Content
            will be accessible to the intended recipient(s). If you elect to
            make User Content publicly available, you acknowledge and agree that
            such User Content may be accessible, viewable, copied, distributed,
            and used by any person or entity on the internet, and that Listening
            has no control over, and assumes no responsibility or liability for,
            any use or misuse of such publicly available User Content.
          </p>
          <p className='mb-3 text-black'>
            By sharing or making User Content publicly available through the
            Service, you represent and warrant that you have all necessary
            rights, licenses, consents, and permissions to do so, and that such
            sharing or public availability does not violate any applicable law
            or the rights of any third party, including intellectual property,
            privacy, or publicity rights. You further agree to indemnify,
            defend, and hold harmless Listening and its affiliates from and
            against any and all claims, damages, liabilities, losses, costs, and
            expenses (including reasonable attorneys' fees) arising out of or
            relating to your sharing or public dissemination of User Content.
          </p>
          <p className='mb-3 text-black'>
            You acknowledge that Listening complies with the Digital Millennium
            Copyright Act ("DMCA") and maintains a policy of terminating, in
            appropriate circumstances, users who are repeat infringers. By using
            the sharing or public availability features of the Service, you
            agree that your conduct is subject to Listening's DMCA policy and
            enforcement procedures, including the potential removal of User
            Content and suspension or termination of your account.
          </p>

          <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
            9. <strong>Communications.</strong>
          </h2>
          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            9.1. <strong>Push Notifications.</strong>
          </h3>
          <p className='mb-3 text-black'>
            When you install our app on your mobile device, you agree to receive
            push notifications, which are messages an app sends you on your
            mobile device when you are not in the app. You can turn off
            notifications by visiting your mobile device's "settings" page
          </p>

          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            9.2. <strong>Email.</strong>
          </h3>
          <p className='mb-3 text-black'>
            We may send you emails concerning our products and services, as well
            as those of third parties. You may opt out of promotional emails by
            following the unsubscribe instructions in the promotional email
            itself.
          </p>

          <h2 className='font-inter my-3 text-[14px] leading-[26px]'>
            10. <strong>Prohibited Conduct.</strong> BY USING THE SERVICE, YOU
            AGREE NOT TO:
          </h2>

          <p className='mb-3 ml-8 text-black'>
            10.1 use the Service for any illegal purpose or in violation of any
            local, state, national, or international law;
          </p>
          <p className='mb-3 ml-8 text-black'>
            10.2 harass, threaten, demean, embarrass, bully, or otherwise harm
            any other user of the Service;
          </p>
          <p className='mb-3 ml-8 text-black'>
            10.3 violate, encourage others to violate, or provide instructions
            on how to violate, any right of a third party, including by
            infringing or misappropriating any third-party intellectual property
            right;
          </p>
          <p className='mb-3 ml-8 text-black'>
            10.4 access, search, or otherwise use any portion of the Service
            through the use of any engine, software, tool, agent, device, or
            mechanism (including spiders, robots, crawlers, and data mining
            tools) other than the software or search agents provided by
            Listening;
          </p>
          <p className='mb-3 ml-8 text-black'>
            10.5 interfere with security-related features of the Service,
            including by: (i) disabling or circumventing features that prevent
            or limit use, printing or copying of any content; or (ii) reverse
            engineering or otherwise attempting to discover the source code of
            any portion of the Service except to the extent that the activity is
            expressly permitted by applicable law;
          </p>
          <p className='mb-3 ml-8 text-black'>
            10.6 interfere with the operation of the Service or any user's
            enjoyment of the Service, including by: (i) uploading or otherwise
            disseminating any virus, adware, spyware, worm, or other malicious
            code; (ii) making any unsolicited offer or advertisement to another
            user of the Service; (iii) collecting personal information about
            another user or third party without consent; or (iv) interfering
            with or disrupting any network, equipment, or server connected to or
            used to provide the Service;
          </p>
          <p className='mb-3 ml-8 text-black'>
            10.7 perform any fraudulent activity including impersonating any
            person or entity, claiming a false affiliation or identity,
            accessing any other Service account without permission, or
            falsifying your age or date of birth;
          </p>
          <p className='mb-3 ml-8 text-black'>
            10.8 sell or otherwise transfer the access granted under these Terms
            or any Materials (as defined in Section 6 (Ownership; Proprietary
            Rights)) or any right or ability to view, access, or use any
            Materials; or
          </p>
          <p className='mb-3 ml-8 text-black'>
            10.9 attempt to do any of the acts described in this Section 10
            (Prohibited Conduct) or assist or permit any person in engaging in
            any of the acts described in this Section 10 (Prohibited Conduct).
          </p>

          <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
            11. <strong>Intellectual Property Rights Protection</strong>
          </h2>

          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            11.1. <strong>Respect of Third Party Rights.</strong>
          </h3>
          <p className='mb-3 text-black'>
            Listening respects the intellectual property (IP) rights of others,
            takes the protection of IP rights very seriously, and requires users
            of the Service to do the same. Infringing activity will not be
            tolerated on the Service.
          </p>

          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            11.2. <strong>DMCA Notification.</strong>
          </h3>
          <p className='mb-3 text-black'>
            We comply with the provisions of the Digital Millennium Copyright
            Act (DMCA), 17 U.S.C. § 512. If you have an IP complaint about
            material on the Service, you may contact our Designated Agent at the
            following address:
          </p>
          <p className='ml-8'>
            The Listening App Inc.
            <br />
            Attn: Derek Wild
            <br />
            447 Sutter Street Suite 506, #1125, San Francisco, CA 94108
            <br />
            Email:{' '}
            <a
              className='text-blue-600 underline'
              href='mailto:ip-infringement@listening.com#462f366b2f2820342f2821232b232832062a2f353223282f28216825292b'
            >
              <span data-cfemail='e38a93ce8a8d85918a8d84868e868d97a38f8a9097868d8a8d84cd808c8e'>
                ip-infringement@listening.com
              </span>
            </a>
            <br />
            <span className='text-blue-600 underline'>Phone: +18443559887</span>
          </p>

          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            11.3. <strong>Procedure for Reporting Claimed Infringement.</strong>
          </h3>
          <p className='mb-3 text-black'>
            If you believe that content on the Service infringes your IP rights,
            you may send a written "
            <strong>
              <u>Notification of Claimed Infringement</u>
            </strong>
            " to the Designated Agent identified above. To be effective, the
            notification must contain substantially the following:
          </p>
          <h3 className='mb-3 ml-8 text-black'>
            (a) an electronic or physical signature of a person authorized to
            act on behalf of the owner of the copyright or other IP that is
            allegedly infringed;
          </h3>
          <h3 className='mb-3 ml-8 text-black'>
            (b) identification of the copyrighted work or other IP right that
            you claim has been infringed;
          </h3>
          <h3 className='mb-3 ml-8 text-black'>
            (c) identification of the material that you claim is infringing and
            a link to where it is located on the Service;
          </h3>
          <h3 className='mb-3 ml-8 text-black'>
            (d) your legal name and contact information (address, telephone
            number, and email address);
          </h3>
          <h3 className='mb-3 ml-8 text-black'>
            (e) a statement that you have a good faith belief that the use of
            the material on the Service in the manner complained of is not
            authorized by the copyright or other IP right owner, its agent, or
            the law; and
          </h3>
          <h3 className='mb-3 ml-8 text-black'>
            (f) a statement by you that the above information in your notice is
            accurate and that, under penalty of perjury, you are the copyright
            or other IP right owner or authorized to act on the copyright or IP
            owner's behalf.
          </h3>
          <p className='mb-3 text-black'>
            You consent to Listening sharing your Notification of Claimed
            Infringement with the allegedly infringing user as well as with
            publicly available databases that track notifications of claimed
            infringement.
          </p>

          <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
            11.4. <strong>Counter Notification.</strong>
          </h3>
          <p className='mb-3 text-black'>
            If you receive a notification from Listening that material made
            available by you on the Service has been the subject of a
            Notification of Claimed Infringement, you may provide Listening with
            a "<strong>Counter Notification.</strong> ." To be effective, a
            Counter Notification must be in writing, provided to Listening's
            Designated Agent (Section 11.2), and include substantially the
            following information:
            <h3 className='mb-3 ml-8 text-black'>
              (a) your legal name and contact information (address, and
              telephone number, and email address);
            </h3>
            <h3 className='mb-3 ml-8 text-black'>
              (b) your physical or electronic signature;
            </h3>
            <h3 className='mb-3 ml-8 text-black'>
              (c) identification of the material that has been removed or to
              which access has been disabled and the location at which the
              material appeared before it was removed or access to it was
              disabled;
            </h3>
            <h3 className='mb-3 ml-8 text-black'>
              (d) a statement under penalty of perjury that you have a good
              faith belief that the material was removed or disabled as a result
              of mistake or misidentification; and
            </h3>
            <h3 className='mb-3 ml-8 text-black'>
              (e) a statement that you consent to the jurisdiction of Federal
              District Court for the judicial district in which the address is
              located, or if you are residing outside of the United States, then
              for any judicial district in which Listening may be found, and
              that you will accept service of process from the person who
              provided notification under Section 11.2 (
              <em>DMCA Notification</em>) above or an agent of that person.
            </h3>
            <p className='mb-3 text-black'>
              Note that Listening does not provide legal advice. A party
              submitting a Counter Notification may want to consult a lawyer or
              see 17 U.S.C. § 512 to confirm its obligations to provide a valid
              counter notification.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              11.5.{' '}
              <strong>
                Reposting of Content Subject to a Counter Notification.
              </strong>
            </h3>
            <p className='mb-3 text-black'>
              If you submit a Counter Notification, then Listening will promptly
              provide the person who submitted the Notification of Claimed
              Infringement with a copy of your Counter Notification and inform
              that person that Listening will replace the removed User Content
              or cease disabling access to it in 10 business days. Listening
              will replace the removed User Content and cease disabling access
              to it not less than 10, nor more than 14, business days following
              receipt of the Counter Notification, unless Listening's Designated
              Agent receives notice from the party that submitted the
              Notification of Claimed Infringement that such person has filed an
              action seeking a court order to restrain the user from engaging in
              infringing activity relating to the material on Listening's system
              or network.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              11.6. <strong>Repeat Infringers (Three Strikes Policy).</strong>
            </h3>
            <p className='mb-3 text-black'>
              Listening maintains a policy of terminating, in appropriate
              circumstances, users who are repeat infringers of intellectual
              property rights. In accordance with this policy, Listening will:
              (a) remove or disable access to material that is the subject of a
              valid notice under the Digital Millennium Copyright Act ("DMCA");
              and (b) maintain records of DMCA notices on a per-user basis.
            </p>
            <p className='mb-3 text-black'>
              Listening implements a "three strikes" policy under which users
              who are the subject of multiple valid DMCA notices may have their
              accounts suspended or terminated. Notwithstanding the foregoing,
              Listening reserves the right, in its sole discretion, to suspend
              or terminate a user's account at any time if it determines that
              the user has engaged in willful, egregious, or repeated
              infringement, regardless of the number of notices received.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              11.7.{' '}
              <strong>
                False Notifications of Claimed Infringement or Counter
                Notifications.
              </strong>
            </h3>
            <p className='mb-3 text-black'>
              Under the DMCA, Listening has the right to seek civil penalties
              (damages, costs, and attorneys' fees) from any party that submits
              a Notification of Claimed Infringement or Counter Notification
              while knowing that it contains material misrepresentations.
            </p>
            <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
              12. <strong>Modification of Terms</strong>
            </h2>
            <p className='mb-3 text-black'>
              We may, from time to time, change these Terms. Please check these
              Terms periodically for changes. Revisions will be effective
              immediately except that, for existing users, material revisions
              will be effective 30 days after posting or notice to you of the
              revisions unless otherwise stated. We may require that you accept
              modified Terms in order to continue to use the Service. If you do
              not agree to the modified Terms, then you should delete your
              Listening account, remove your User Content, and discontinue your
              use of the Service. Except as expressly permitted in this Section
              12 (<em>Modification of Terms</em>), these Terms may be amended
              only by a written agreement signed by authorized representatives
              of the parties to these Terms.
            </p>
            <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
              13.{' '}
              <strong>
                Term, Termination, and Modification of the Service.
              </strong>
            </h2>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              13.1. <strong>Term.</strong>
            </h3>
            <p className='mb-3 text-black'>
              These Terms are effective beginning when you accept the Terms or
              first download, install, access, or use the Service, and ending
              when terminated as described in Section 13.2 (<em>Termination</em>
              ).
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              13.2. <strong>Termination.</strong>
            </h3>
            <p className='mb-3 text-black'>
              If you violate any provision of these Terms, then your
              authorization to access the Service and these Terms automatically
              terminate. In addition, Listening may, at its sole discretion,
              terminate these Terms or your account on the Service, or suspend
              or terminate your access to the Service, at any time for any
              reason or no reason, with or without notice, and without any
              liability to you arising from such termination. You may terminate
              your account and these Terms at any time through Listening's web
              app or your App Store's subscription management screen, or by
              contacting customer service a{' '}
              <a
                className='text-blue-600 underline'
                href='mailto:support@listening.com'
              >
                <span>support@listening.com</span>
              </a>
              .
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              13.3. <strong>Effect of Termination.</strong>
            </h3>
            <p className='mb-3 text-black'>
              Upon termination of these Terms: (a) your license rights will
              terminate and you must immediately cease all use of the Service;
              (b) you will no longer be authorized to access your account or the
              Service; (c) you must pay Listening any unpaid amount that was due
              prior to termination; and (d) all payment obligations accrued
              prior to termination and Sections 5.3 (<em>Feedback</em>), 6 (
              <em>Ownership; Proprietary Rights</em>), 13.3 (
              <em>Effect of Termination</em>), 14 (<em>Indemnity</em>), 15 (
              <em>Disclaimers; No Warranties by Listening</em>), 16 (
              <em>Limitation of Liability</em>), 17 (
              <em>Dispute Resolution and Arbitration</em>), and 18 (
              <em>Miscellaneous</em>) will survive. You are solely responsible
              for retaining copies of any User Content you Post to the Service
              since upon termination of your account, you may lose access rights
              to any User Content you Posted to the Service. If your account has
              been terminated for a breach of these Terms, then you are
              prohibited from creating a new account on the Service using a
              different name, email address or other forms of account
              verification.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              13.4. <strong>Modification of the Service.</strong>
            </h3>
            <p className='mb-3 text-black'>
              Listening reserves the right to modify or discontinue all or any
              portion of the Service at any time (including by limiting or
              discontinuing certain features of the Service), temporarily or
              permanently, without notice to you. Listening will have no
              liability for any change to the Service, including any paid-for
              functionalities of the Service, or any suspension or termination
              of your access to or use of the Service. You should retain copies
              of any User Content you Post to the Service so that you have
              permanent copies in the event the Service is modified in such a
              way that you lose access to User Content you Posted to the
              Service.
            </p>
            <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
              14. <strong>Indemnity.</strong>
            </h2>
            <p className='mb-3 text-black'>
              To the fullest extent permitted by law, you are responsible for
              your use of the Service, and you will defend and indemnify
              Listening, its affiliates and their respective shareholders,
              directors, managers, members, officers, employees, consultants,
              and agents (together, the "
              <strong>
                <u>Listening Entities</u>
              </strong>
              "), from and against every claim brought by a third party, and any
              related liability, damage, loss, and expense, including attorneys'
              fees and costs, arising out of or connected with: (1) your
              unauthorized use of, or misuse of, the Service; (2) your violation
              of any portion of these Terms, any representation, warranty, or
              agreement referenced in these Terms, or any applicable law or
              regulation; (3) your violation of any third-party right, including
              any intellectual property right or publicity, confidentiality,
              other property, or privacy right; or (4) any dispute or issue
              between you and any third party. We reserve the right, at our own
              expense, to assume the exclusive defense and control of any matter
              otherwise subject to indemnification by you (without limiting your
              indemnification obligations with respect to that matter), and in
              that case, you agree to cooperate with our defense of those
              claims.
            </p>
            <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
              15. <strong>Disclaimers; No Warranties by Listening.</strong>
            </h2>
            <p className='mb-3 text-black'>
              15.1 THE SERVICE AND ALL MATERIALS AND CONTENT AVAILABLE THROUGH
              THE SERVICE ARE PROVIDED "AS IS" AND ON AN "AS AVAILABLE" BASIS.
              LISTENING DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR
              IMPLIED, RELATING TO THE SERVICE AND ALL MATERIALS AND CONTENT
              AVAILABLE THROUGH THE SERVICE, INCLUDING: (A) ANY IMPLIED WARRANTY
              OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, QUIET
              ENJOYMENT, OR NON-INFRINGEMENT; AND (B) ANY WARRANTY ARISING OUT
              OF COURSE OF DEALING, USAGE, OR TRADE. LISTENING DOES NOT WARRANT
              THAT THE SERVICE OR ANY PORTION OF THE SERVICE, OR ANY MATERIALS
              OR CONTENT OFFERED THROUGH THE SERVICE, WILL BE UNINTERRUPTED,
              SECURE, OR FREE OF ERRORS, VIRUSES, OR OTHER HARMFUL COMPONENTS,
              AND LISTENING DOES NOT WARRANT THAT ANY OF THOSE ISSUES WILL BE
              CORRECTED.
            </p>
            <p className='mb-3 text-black'>
              15.2 NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED
              BY YOU FROM THE SERVICE OR LISTENING ENTITIES OR ANY MATERIALS OR
              CONTENT AVAILABLE THROUGH THE SERVICE WILL CREATE ANY WARRANTY
              REGARDING ANY OF THE LISTENING ENTITIES OR THE SERVICE THAT IS NOT
              EXPRESSLY STATED IN THESE TERMS. WE ARE NOT RESPONSIBLE FOR ANY
              DAMAGE THAT MAY RESULT FROM THE SERVICE AND YOUR DEALING WITH ANY
              OTHER SERVICE USER. YOU UNDERSTAND AND AGREE THAT YOU USE ANY
              PORTION OF THE SERVICE AT YOUR OWN DISCRETION AND RISK, AND THAT
              WE ARE NOT RESPONSIBLE FOR ANY DAMAGE TO YOUR PROPERTY (INCLUDING
              YOUR COMPUTER SYSTEM OR MOBILE DEVICE USED IN CONNECTION WITH THE
              SERVICE) OR ANY LOSS OF DATA, INCLUDING USER CONTENT.
            </p>
            <p className='mb-3 text-black'>
              15.3 THE LIMITATIONS, EXCLUSIONS AND DISCLAIMERS IN THIS SECTION
              15 (<em>DISCLAIMERS; NO WARRANTIES BY LISTENING</em>) APPLY TO THE
              FULLEST EXTENT PERMITTED BY LAW. Listening does not disclaim any
              warranty or other right that Listening is prohibited from
              disclaiming under
            </p>
            <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
              16. <strong>Limitation of Liability.</strong>
            </h2>
            <p className='mb-3 text-black'>
              16.1 TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL THE
              LISTENING ENTITIES BE LIABLE TO YOU FOR ANY INDIRECT, INCIDENTAL,
              SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES (INCLUDING DAMAGES FOR
              LOSS OF PROFITS, GOODWILL, OR ANY OTHER INTANGIBLE LOSS) ARISING
              OUT OF OR RELATING TO YOUR ACCESS TO OR USE OF, OR YOUR INABILITY
              TO ACCESS OR USE, THE SERVICE OR ANY MATERIALS OR CONTENT ON THE
              SERVICE, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING
              NEGLIGENCE), STATUTE, OR ANY OTHER LEGAL THEORY, AND WHETHER OR
              NOT ANY LISTENING ENTITY HAS BEEN INFORMED OF THE POSSIBILITY OF
              DAMAGE.
            </p>
            <p className='mb-3 text-black'>
              16.2 EXCEPT AS PROVIDED IN SECTIONS 17.5 (
              <em>COMMENCING ARBITRATION</em>) AND 17.7 (
              <em>ARBITRATION RELIEF</em>) AND TO THE FULLEST EXTENT PERMITTED
              BY LAW, THE AGGREGATE LIABILITY OF THE LISTENING ENTITIES TO YOU
              FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE USE OF OR ANY
              INABILITY TO USE ANY PORTION OF THE SERVICE OR OTHERWISE UNDER
              THESE TERMS, WHETHER IN CONTRACT, TORT, OR OTHERWISE, IS LIMITED
              TO THE GREATER OF: (A) THE AMOUNT YOU HAVE PAID TO LISTENING FOR
              ACCESS TO AND USE OF THE SERVICE IN THE 12 MONTHS PRIOR TO THE
              EVENT OR CIRCUMSTANCE GIVING RISE TO THE CLAIM AND (B) US$100.
            </p>
            <p className='mb-3 text-black'>
              16.3 EACH PROVISION OF THESE TERMS THAT PROVIDES FOR A LIMITATION
              OF LIABILITY, DISCLAIMER OF WARRANTIES, OR EXCLUSION OF DAMAGES IS
              INTENDED TO AND DOES ALLOCATE THE RISKS BETWEEN THE PARTIES UNDER
              THESE TERMS. THIS ALLOCATION IS AN ESSENTIAL ELEMENT OF THE BASIS
              OF THE BARGAIN BETWEEN THE PARTIES. EACH OF THESE PROVISIONS IS
              SEVERABLE AND INDEPENDENT OF ALL OTHER PROVISIONS OF THESE TERMS.
              THE LIMITATIONS IN THIS SECTION 16 (
              <em>LIMITATION OF LIABILITY</em>) WILL APPLY EVEN IF ANY LIMITED
              REMEDY FAILS OF ITS ESSENTIAL PURPOSE.
            </p>
            <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
              17. <strong>Dispute Resolution and Arbitration.</strong>
            </h2>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              17.1. <strong>Generally.</strong>
            </h3>
            <p className='mb-3 text-black'>
              Except as described in Section 17.2 (<em>Exceptions</em>) and 17.3
              (<em>Opt-Out</em>), you and Listening agree that every dispute
              arising in connection with these Terms, the Service, or
              communications from us will be resolved through binding
              arbitration. Arbitration uses a neutral arbitrator instead of a
              judge or jury, is less formal than a court proceeding, may allow
              for more limited discovery than in court, and is subject to very
              limited review by courts. This agreement to arbitrate disputes
              includes all claims whether based in contract, tort, statute,
              fraud, misrepresentation, or any other legal theory, and
              regardless of whether a claim arises during or after the
              termination of these Terms. Any dispute relating to the
              interpretation, applicability, or enforceability of this binding
              arbitration agreement will be resolved by the arbitrator.
            </p>
            <p>
              <span className='font-normal text-black'>
                YOU UNDERSTAND AND AGREE THAT, BY ENTERING INTO THESE TERMS, YOU
                AND Listening ARE EACH WAIVING THE RIGHT TO A TRIAL BY JURY OR
                TO PARTICIPATE IN A CLASS ACTION.
              </span>
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              17.2. <strong>Exceptions.</strong>
            </h3>
            <p className='mb-3 text-black'>
              Although we are agreeing to arbitrate most disputes between us,
              nothing in these Terms will be deemed to waive, preclude, or
              otherwise limit the right of either party to: (a) bring an
              individual action in small claims court; (b) pursue an enforcement
              action through the applicable federal, state, or local agency if
              that action is available; (c) seek injunctive relief in a court of
              law in aid of arbitration; or (d) to file suit in a court of law
              to address an intellectual property infringement claim.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              17.3. <strong>Opt-Out.</strong>
            </h3>
            <p className='mb-3 text-black'>
              If you do not wish to resolve disputes by binding arbitration, you
              may opt out of the provisions of this Section 17 (
              <em>Dispute Resolution and Arbitration</em>) within 30 days after
              the date that you agree to these Terms by sending a letter to The
              Listening App Inc., Attention: Legal Department – Arbitration
              Opt-Out, 880 Harrison St, San Francisco, CA 94107 that specifies:
              your full legal name, the email address associated with your
              account on the Service, and a statement that you wish to opt out
              of arbitration ("
              <strong>
                <u>Opt-Out Notice</u>
              </strong>
              "). Once Listening receives your Opt-Out Notice, this Section 17 (
              <em>Dispute Resolution and Arbitration</em> will be void and any
              action arising out of these Terms will be resolved as set forth in
              Section 18.2 (<em>Governing Law</em>). The remaining provisions of
              these Terms will not be affected by your Opt-Out Notice.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              17.4. <strong>Arbitrator.</strong>
            </h3>
            <p className='mb-3 text-black'>
              This arbitration agreement, and any arbitration between us, is
              subject the Federal Arbitration Act and will be administered by
              the American Arbitration Association ("
              <strong>
                <u>AAA</u>
              </strong>
              ") under its Consumer Arbitration Rules (collectively, "
              <strong>
                <u>AAA Rules</u>
              </strong>
              ") as modified by these Terms. The AAA Rules and filing forms are
              available online at www.adr.org, by calling the AAA at
              +1-800-778-7879, or by contacting Listening.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              17.5. <strong>Commencing Arbitration.</strong>
            </h3>
            <p className='mb-3 text-black'>
              Before initiating arbitration, a party must first send a written
              notice of the dispute to the other party by certified U.S. Mail or
              by Federal Express (signature required) or, only if that other
              party has not provided a current physical address, then by
              electronic mail ("
              <strong>
                <u>Notice of Arbitration</u>
              </strong>
              "). Listening's address for Notice is: The Listening App Inc., 880
              Harrison St, San Francisco, CA 94107. The Notice of Arbitration
              must: (a) identify the name or account number of the party making
              the claim; (b) describe the nature and basis of the claim or
              dispute; and (c) set forth the specific relief sought ("
              <strong>
                <u>Demand</u>
              </strong>
              "). The parties will make good faith efforts to resolve the claim
              directly, but if the parties do not reach an agreement to do so
              within 30 days after the Notice of Arbitration is received, you or
              Listening may commence an arbitration proceeding. If you commence
              arbitration in accordance with these Terms, Listening will
              reimburse you for your payment of the filing fee, unless your
              claim is for more than US$10,000 or if the Company has received 25
              or more similar demands for arbitration, in which case the payment
              of any fees will be decided by the AAA Rules. If the arbitrator
              finds that either the substance of the claim or the relief sought
              in the Demand is frivolous or brought for an improper purpose (as
              measured by the standards set forth in Federal Rule of Civil
              Procedure 11(b)), then the payment of all fees will be governed by
              the AAA Rules and the other party may seek reimbursement for any
              fees paid to AAA.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              17.6. <strong>Arbitration Proceedings.</strong>
            </h3>
            <p className='mb-3 text-black'>
              Any arbitration hearing will take place in the county and state of
              your residence unless we agree otherwise or, if the claim is for
              US$10,000 or less (and does not seek injunctive relief), you may
              choose whether the arbitration will be conducted: (a) solely on
              the basis of documents submitted to the arbitrator; (b) through a
              telephonic or video hearing; or (c) by an in-person hearing as
              established by the AAA Rules in the county (or parish) of your
              residence. During the arbitration, the amount of any settlement
              offer made by you or Listening must not be disclosed to the
              arbitrator until after the arbitrator makes a final decision and
              award, if any. Regardless of the manner in which the arbitration
              is conducted, the arbitrator must issue a reasoned written
              decision sufficient to explain the essential findings and
              conclusions on which the decision and award, if any, are based.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              17.7. <strong>Arbitration Relief.</strong>
            </h3>
            <p className='mb-3 text-black'>
              Except as provided in Section 17.8 (<em>No Class Actions</em>),
              the arbitrator can award any relief that would be available if the
              claims had been brought in a court of competent jurisdiction. If
              the arbitrator awards you an amount higher than the last written
              settlement amount offered by Listening before an arbitrator was
              selected, Listening will pay to you the higher of: (a) the amount
              awarded by the arbitrator and (b) US$10,000. The arbitrator's
              award shall be final and binding on all parties, except (1) for
              judicial review expressly permitted by law or (2) if the
              arbitrator's award includes an award of injunctive relief against
              a party, in which case that party shall have the right to seek
              judicial review of the injunctive relief in a court of competent
              jurisdiction that shall not be bound by the arbitrator's
              application or conclusions of law. Judgment on the award may be
              entered in any court having jurisdiction.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              17.8. <strong>No Class Actions.</strong>
            </h3>
            <p className='mb-3 text-black'>
              YOU AND LISTENING AGREE THAT EACH MAY BRING CLAIMS AGAINST THE
              OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A
              PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE
              PROCEEDING. Further, unless both you and Listening agree
              otherwise, the arbitrator may not consolidate more than one
              person's claims, and may not otherwise preside over any form of a
              representative or class proceeding.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              17.9.{' '}
              <strong>Modifications to this Arbitration Provision.</strong>
            </h3>
            <p className='mb-3 text-black'>
              If Listening makes any substantive change to this arbitration
              provision, you may reject the change by sending us written notice
              within 30 days of the change to Listening's address for Notice of
              Arbitration, in which case your account with Listening will be
              immediately terminated and this arbitration provision, as in
              effect immediately prior to the changes you rejected will survive.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              17.10. <strong>Enforceability.</strong>
            </h3>
            <p className='mb-3 text-black'>
              If Section 17.8 (<em>No Class Actions</em>) or the entirety of
              this Section 17 (<em>Dispute Resolution and Arbitration</em>) is
              found to be unenforceable, or if Listening receives an Opt-Out
              Notice from you, then the entirety of this Section 17 (
              <em>Dispute Resolution and Arbitration</em>) will be null and void
              and, in that case, the exclusive jurisdiction and venue described
              in Section 18.2 (<em>Governing Law</em>) will govern any action
              arising out of or related to these Terms.
            </p>
            <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
              18. <strong>Miscellaneous.</strong>
            </h2>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              18.1. <strong>General Terms.</strong>
            </h3>
            <p className='mb-3 text-black'>
              These Terms, including the Privacy Policy and any other agreements
              expressly incorporated by reference into these Terms, are the
              entire and exclusive understanding and agreement between you and
              Listening regarding your use of the Service. You may not assign or
              transfer these Terms or your rights under these Terms, in whole or
              in part, by operation of law or otherwise, without our prior
              written consent. We may assign these Terms and all rights granted
              under these Terms, including with respect to your User Content, at
              any time without notice or consent. The failure to require
              performance of any provision will not affect our right to require
              performance at any other time after that, nor will a waiver by us
              of any breach or default of these Terms, or any provision of these
              Terms, be a waiver of any subsequent breach or default or a waiver
              of the provision itself. Use of Section headers in these Terms is
              for convenience only and will not have any impact on the
              interpretation of any provision. Throughout these Terms the use of
              the word "including" means "including but not limited to." If any
              part of these Terms is held to be invalid or unenforceable, then
              the unenforceable part will be given effect to the greatest extent
              possible, and the remaining parts will remain in full force and
              effect.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              18.2. <strong>Governing Law.</strong>
            </h3>
            <p className='mb-3 text-black'>
              These Terms are governed by the laws of the State of California
              without regard to conflict of law principles. You and Listening
              submit to the personal and exclusive jurisdiction of the state
              courts and federal courts located within San Francisco, California
              for resolution of any lawsuit or court proceeding permitted under
              these Terms. We operate the Service from our offices in
              California, and we make no representation that Materials included
              in the Service are appropriate or available for use in other
              locations.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              18.3. <strong>Privacy Policy.</strong>
            </h3>
            <p className='mb-3 text-black'>
              Please read the Listening{' '}
              <a
                className='text-blue-600 underline'
                href='https://www.listening.com/privacy'
              >
                <u>Privacy Policy</u>
              </a>{' '}
              (the "
              <strong>
                <u>Privacy Policy</u>
              </strong>
              ") carefully for information relating to our collection, use,
              storage, and disclosure of your personal information. The
              Listening Privacy Policy is incorporated by this reference into,
              and made a part of, these Terms.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              18.4. <strong>Additional Terms.</strong>
            </h3>
            <p className='mb-3 text-black'>
              Your use of the Service is subject to all additional terms,
              policies, rules, or guidelines applicable to the Service or
              certain features of the Service that we may post on or link to
              from the Service ("
              <strong>
                <u>Additional Terms</u>
              </strong>
              "). All Additional Terms are incorporated by this reference into,
              and made a part of, these Terms.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              18.5. <strong>Consent to Electronic Communications.</strong>
            </h3>
            <p className='mb-3 text-black'>
              By using the Service, you consent to receiving certain electronic
              communications from us as further described in our Privacy Policy.
              Please read our Privacy Policy to learn more about our electronic
              communications practices. You agree that any notices, agreements,
              disclosures, or other communications that we send to you
              electronically will satisfy any legal communication requirements,
              including that those communications be in writing.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              18.6. <strong>Contact Information.</strong>
            </h3>
            <p className='mb-3 text-black'>
              The Service is offered by The Listening App Inc., located at 880
              Harrison St, San Francisco, CA 94107. You may contact us by
              sending correspondence to that address or by emailing us at{' '}
              <a
                className='text-blue-600 underline'
                href='mailto:support@listening.com'
              >
                <span data-cfemail='92e1e7e2e2fde0e6d2fefbe1e6f7fcfbfcf5bcf1fdff'>
                  support@listening.com
                </span>
              </a>
              .
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              18.7. <strong>Notice to California Residents.</strong>
            </h3>
            <p className='mb-3 text-black'>
              If you are a California resident, then under California Civil Code
              Section 1789.3, you may contact the Complaint Assistance Unit of
              the Division of Consumer Services of the California Department of
              Consumer Affairs in writing at 1625 N. Market Blvd., Suite N 112,
              Sacramento, California 95834, or by telephone at +1-800-952-5210
              in order to resolve a complaint regarding the Service or to
              receive further information regarding use of the Service.
            </p>
            <h3 className='font-inter mt-3 text-[14px] leading-[26px]'>
              18.8. <strong>No Support.</strong>
            </h3>
            <p className='mb-3 text-black'>
              We are under no obligation to provide support for the Service. In
              instances where we may offer support, the support will be subject
              to published policies.
            </p>
            <h2 className='font-inter mt-3 text-[14px] leading-[26px]'>
              19. <strong>Notice Regarding Apple.</strong>
            </h2>
            <p className='mb-3 text-black'>
              This Section 19 (<em>Notice Regarding Apple</em>) only applies to
              the extent you are using our mobile application on an iOS device.
              You acknowledge that these Terms are between you and Listening
              only, not with Apple Inc. ("
              <strong>
                <u>Apple</u>
              </strong>
              "), and Apple is not responsible for the Service or the content of
              it. Apple has no obligation to furnish any maintenance and support
              services with respect to the Service. If the Service fails to
              conform to any applicable warranty, you may notify Apple, and
              Apple will refund any applicable purchase price for the mobile
              application to you. To the maximum extent permitted by applicable
              law, Apple has no other warranty obligation with respect to the
              Service. Apple is not responsible for addressing any claims by you
              or any third party relating to the Service or your possession
              and/or use of the Service, including: (1) product liability
              claims; (2) any claim that the Service fails to conform to any
              applicable legal or regulatory requirement; or (3) claims arising
              under consumer protection or similar legislation. Apple is not
              responsible for the investigation, defense, settlement, and
              discharge of any third-party claim that the Service and/or your
              possession and use of the Service infringe a third party's
              intellectual property rights. You agree to comply with any
              applicable third-party terms when using the Service. Apple and
              Apple's subsidiaries are third-party beneficiaries of these Terms,
              and upon your acceptance of these Terms, Apple will have the right
              (and will be deemed to have accepted the right) to enforce these
              Terms against you as a third-party beneficiary of these Terms. You
              hereby represent and warrant that: (a) you are not located in a
              country that is subject to a U.S. Government embargo or that has
              been designated by the U.S. Government as a "terrorist supporting"
              country; and (b) you are not listed on any U.S. Government list of
              prohibited or restricted parties.
            </p>
          </p>
        </div>
      </section>
      <Footer />
    </>
  ),
});
